import React, { useState } from 'react';
import { suppliersAPI } from '@/lib/api';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Users, Pencil, Check, X, Plus, Trash2, Search, ChevronUp, ChevronDown, ChevronsUpDown, Link2 } from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const EMPTY_NEW = { name: '', phone: '', email: '' };

// Stable key for a supplier row: UUID if available, name_lower fallback for pre-migration rows
const rowKey = (s) => s.supplier_id || s.id;

const SupplierDetailsTab = () => {
  const qc = useQueryClient();
  const { data: suppliers = [], isLoading } = useQuery({
    queryKey: ['suppliers'],
    queryFn: () => suppliersAPI.getAll().then(r => r.data ?? []),
    staleTime: 5 * 60 * 1000,
  });

  const [editMap, setEditMap]    = useState({});
  const [savingSet, setSaving]   = useState(new Set());
  const [deletingSet, setDeleting] = useState(new Set());
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [adding, setAdding]      = useState(false);
  const [newRow, setNewRow]      = useState(EMPTY_NEW);
  const [addingSaving, setAddingSaving] = useState(false);
  const [search, setSearch]      = useState('');
  const [sortBy, setSortBy]      = useState('name');
  const [sortDir, setSortDir]    = useState('asc');

  const startEdit = (supplier) => {
    const key = rowKey(supplier);
    setEditMap(prev => ({
      ...prev,
      [key]: { phone: supplier.phone || '', email: supplier.email || '' },
    }));
  };

  const cancelEdit = (key) => {
    setEditMap(prev => { const n = { ...prev }; delete n[key]; return n; });
  };

  const handleSave = async (supplier) => {
    const key   = rowKey(supplier);
    const edits = editMap[key];
    if (!edits) return;
    setSaving(s => new Set([...s, key]));
    try {
      if (supplier.supplier_id) {
        // UUID known — use PATCH (contact-only update, name unchanged)
        await suppliersAPI.update(supplier.supplier_id, { phone: edits.phone, email: edits.email });
        qc.setQueryData(['suppliers'], (prev = []) =>
          prev.map(s => rowKey(s) === key ? { ...s, ...edits } : s)
        );
      } else {
        // Pre-migration row without UUID — POST creates entry and assigns UUID
        const res = await suppliersAPI.create({ name: supplier.name, phone: edits.phone, email: edits.email });
        const newSupplierId = res.data?.supplier_id;
        qc.setQueryData(['suppliers'], (prev = []) =>
          prev.map(s => rowKey(s) === key
            ? { ...s, ...edits, supplier_id: newSupplierId }
            : s
          )
        );
      }
      cancelEdit(key);
      toast.success(`${supplier.name} saved`);
    } catch {
      toast.error('Could not save supplier details');
    } finally {
      setSaving(s => { const n = new Set(s); n.delete(key); return n; });
    }
  };

  const handleDelete = (supplier) => {
    if (!supplier.supplier_id) {
      toast.error('Cannot delete a supplier without a UUID — save contact details first to assign one');
      return;
    }
    setDeleteTarget(supplier);
  };

  const confirmDelete = async () => {
    const supplier = deleteTarget;
    setDeleteTarget(null);
    const key = rowKey(supplier);
    setDeleting(s => new Set([...s, key]));
    try {
      await suppliersAPI.delete(supplier.supplier_id);
      qc.setQueryData(['suppliers'], (prev = []) => prev.filter(s => rowKey(s) !== key));
      toast.success(`${supplier.name} deleted`);
    } catch {
      toast.error('Could not delete supplier');
    } finally {
      setDeleting(s => { const n = new Set(s); n.delete(key); return n; });
    }
  };

  const handleAddSave = async () => {
    const name = newRow.name.trim();
    if (!name) { toast.error('Supplier name is required'); return; }
    if (suppliers.some(s => s.name.toLowerCase() === name.toLowerCase())) {
      toast.error('A supplier with this name already exists'); return;
    }
    setAddingSaving(true);
    try {
      const res = await suppliersAPI.create({ name, phone: newRow.phone, email: newRow.email });
      const supplier_id = res.data?.supplier_id;
      qc.setQueryData(['suppliers'], (prev = []) =>
        [...prev, { id: name.toLowerCase(), supplier_id, name, phone: newRow.phone, email: newRow.email }]
          .sort((a, b) => a.name.localeCompare(b.name))
      );
      setAdding(false);
      setNewRow(EMPTY_NEW);
      toast.success(`${name} added`);
    } catch {
      toast.error('Could not add supplier');
    } finally {
      setAddingSaving(false);
    }
  };

  const handleAddCancel = () => { setAdding(false); setNewRow(EMPTY_NEW); };

  const q = search.toLowerCase();
  const visible = suppliers
    .filter(s =>
      !q ||
      s.name?.toLowerCase().includes(q) ||
      s.phone?.includes(search) ||
      s.email?.toLowerCase().includes(q)
    )
    .sort((a, b) => {
      const av = (a[sortBy] ?? '').toLowerCase();
      const bv = (b[sortBy] ?? '').toLowerCase();
      return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-12 bg-muted rounded-xl animate-pulse" />
        ))}
      </div>
    );
  }

  const outstandingBalance = deleteTarget ? Number(deleteTarget.outstanding_balance || 0) : 0;

  return (
    <div className="space-y-3">
      <AlertDialog open={!!deleteTarget} onOpenChange={open => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete &ldquo;{deleteTarget?.name}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                {outstandingBalance > 0 && (
                  <p className="font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    ₹{outstandingBalance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} is still outstanding with this supplier. Deleting will remove this supplier and their outstanding balance from the system.
                  </p>
                )}
                <p>Past purchase records referencing them will not be affected.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600 text-white"
            >
              Delete Supplier
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="flex items-center justify-between gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search suppliers…"
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-border rounded-lg outline-none focus:border-primary"
          />
        </div>
        <p className="text-xs text-muted-foreground hidden lg:block">
          {suppliers.length > 0
            ? `${suppliers.length} supplier${suppliers.length !== 1 ? 's' : ''}`
            : 'No suppliers yet'}
        </p>
        {!adding && (
          <button
            onClick={() => setAdding(true)}
            className="flex items-center gap-1.5 text-xs font-medium text-primary border border-primary/30 bg-primary/5 hover:bg-primary/10 px-3 py-1.5 rounded-lg transition-colors shrink-0"
          >
            <Plus className="h-3.5 w-3.5" />
            Add Supplier
          </button>
        )}
      </div>

      <div className="border border-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/50 text-xs text-muted-foreground border-b border-border">
              <th
                className="text-left px-4 py-3 font-medium cursor-pointer select-none hover:bg-slate-100 transition-colors whitespace-nowrap"
                onClick={() => { setSortDir(d => sortBy === 'name' ? (d === 'asc' ? 'desc' : 'asc') : 'asc'); setSortBy('name'); }}
              >
                Supplier Name
                {sortBy !== 'name' ? <ChevronsUpDown className="w-3 h-3 opacity-30 inline ml-1" /> : sortDir === 'asc' ? <ChevronUp className="w-3 h-3 inline ml-1" /> : <ChevronDown className="w-3 h-3 inline ml-1" />}
              </th>
              <th className="text-left px-4 py-3 font-medium w-28">Portal</th>
              <th className="text-left px-4 py-3 font-medium w-48">Phone</th>
              <th className="text-left px-4 py-3 font-medium w-56">Email</th>
              <th className="w-28 px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {adding && (
              <tr className="bg-primary/5">
                <td className="px-4 py-3">
                  <input
                    autoFocus
                    type="text"
                    value={newRow.name}
                    onChange={e => setNewRow(p => ({ ...p, name: e.target.value }))}
                    placeholder="Supplier name"
                    className="w-full text-sm border border-border rounded-lg px-2.5 py-1 outline-none focus:border-primary"
                  />
                </td>
                <td className="px-4 py-3" />
                <td className="px-4 py-3">
                  <input
                    type="tel"
                    value={newRow.phone}
                    onChange={e => setNewRow(p => ({ ...p, phone: e.target.value }))}
                    placeholder="+91 98765 43210"
                    className="w-full text-sm border border-border rounded-lg px-2.5 py-1 outline-none focus:border-primary"
                  />
                </td>
                <td className="px-4 py-3">
                  <input
                    type="email"
                    value={newRow.email}
                    onChange={e => setNewRow(p => ({ ...p, email: e.target.value }))}
                    placeholder="sales@supplier.com"
                    onKeyDown={e => { if (e.key === 'Enter') handleAddSave(); if (e.key === 'Escape') handleAddCancel(); }}
                    className="w-full text-sm border border-border rounded-lg px-2.5 py-1 outline-none focus:border-primary"
                  />
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5 justify-end">
                    <button
                      onClick={handleAddSave}
                      disabled={addingSaving}
                      className="flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-2.5 py-1 rounded-lg transition-colors disabled:opacity-50"
                    >
                      <Check className="h-3 w-3" />
                      {addingSaving ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      onClick={handleAddCancel}
                      className="p-1 text-muted-foreground hover:text-foreground rounded transition-colors"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            )}

            {visible.length === 0 && !adding && (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">
                  <Users className="h-8 w-8 mx-auto mb-2 opacity-25" />
                  <p className="text-sm">{search ? 'No suppliers match your search' : 'No suppliers yet'}</p>
                  {!search && <p className="text-xs mt-1">Add one manually or record a purchase to auto-populate.</p>}
                </td>
              </tr>
            )}

            {visible.map(supplier => {
              const key      = rowKey(supplier);
              const editing  = key in editMap;
              const saving   = savingSet.has(key);
              const deleting = deletingSet.has(key);
              const edits    = editMap[key] || {};

              return (
                <tr key={key} className="hover:bg-muted/20 transition-colors">
                  <td className="px-4 py-3 font-medium text-foreground">{supplier.name}</td>

                  <td className="px-4 py-3">
                    {supplier.is_portal_connected ? (
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 font-medium">
                        <Link2 className="w-3 h-3" />
                        Connected
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>

                  <td className="px-4 py-3">
                    {editing ? (
                      <input
                        type="tel"
                        value={edits.phone}
                        onChange={e => setEditMap(prev => ({
                          ...prev,
                          [key]: { ...prev[key], phone: e.target.value },
                        }))}
                        placeholder="+91 98765 43210"
                        className="w-full text-sm border border-border rounded-lg px-2.5 py-1 outline-none focus:border-primary"
                      />
                    ) : (
                      <span className={supplier.phone ? 'text-foreground' : 'text-muted-foreground italic text-xs'}>
                        {supplier.phone || 'Not added'}
                      </span>
                    )}
                  </td>

                  <td className="px-4 py-3">
                    {editing ? (
                      <input
                        type="email"
                        value={edits.email}
                        onChange={e => setEditMap(prev => ({
                          ...prev,
                          [key]: { ...prev[key], email: e.target.value },
                        }))}
                        placeholder="sales@supplier.com"
                        className="w-full text-sm border border-border rounded-lg px-2.5 py-1 outline-none focus:border-primary"
                      />
                    ) : (
                      <span className={supplier.email ? 'text-foreground' : 'text-muted-foreground italic text-xs'}>
                        {supplier.email || 'Not added'}
                      </span>
                    )}
                  </td>

                  <td className="px-4 py-3">
                    {editing ? (
                      <div className="flex items-center gap-1.5 justify-end">
                        <button
                          onClick={() => handleSave(supplier)}
                          disabled={saving}
                          className="flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-2.5 py-1 rounded-lg transition-colors disabled:opacity-50"
                        >
                          <Check className="h-3 w-3" />
                          {saving ? 'Saving…' : 'Save'}
                        </button>
                        <button
                          onClick={() => cancelEdit(key)}
                          className="p-1 text-muted-foreground hover:text-foreground rounded transition-colors"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 justify-end">
                        <button
                          onClick={() => startEdit(supplier)}
                          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground border border-border px-2.5 py-1 rounded-lg transition-colors"
                        >
                          <Pencil className="h-3 w-3" />
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(supplier)}
                          disabled={deleting}
                          className="p-1 text-muted-foreground hover:text-red-600 rounded transition-colors disabled:opacity-40"
                          title="Delete supplier"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SupplierDetailsTab;
